import { isValidPhoneNumber, formatNumber } from 'libphonenumber-js';

import { OrganisationAuthSettings, State } from './types';

const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const PHONE_NUMBER_PATTERN = /\+[\d\s]{9,15}$/i;
const WILDCARD_PATH_SUFFIX = '/*';
const EMAIL_OR_PHONE_NUMBER_PATTERN = new RegExp(
  `^(?:${EMAIL_PATTERN.source}|${PHONE_NUMBER_PATTERN.source})$`,
  [...new Set([EMAIL_PATTERN.flags, PHONE_NUMBER_PATTERN.flags])].join('')
);

const encodeState = (state: State): string => btoa(JSON.stringify(state));
const decodeState = (state: string | null): State => (state ? JSON.parse(atob(state)) : {});

const getClientId = (settings?: OrganisationAuthSettings): string => {
  if (!settings?.client_id) {
    throw new Error('No auth client ID found');
  }
  return settings.client_id;
};

const redirectWhitelistCheck = (pathname: string, whitelist: readonly string[]): boolean => {
  if (whitelist.length === 0) return true;

  return whitelist.some((rule) => {
    if (rule.endsWith(WILDCARD_PATH_SUFFIX)) {
      const base = rule.slice(0, -WILDCARD_PATH_SUFFIX.length);
      return pathname === base || pathname.startsWith(`${base}/`);
    }

    return pathname === rule;
  });
};

const sanitizeRedirect = (redirect?: string | null): string | undefined => {
  if (!redirect) return undefined;
  if (!redirect.startsWith('/') || redirect.startsWith('//') || redirect.includes('\\')) return undefined;

  try {
    const parsed = new URL(redirect, globalThis.location.origin);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return undefined;
  }
};

const validateRedirect = (redirect?: string | null, whitelist: readonly string[] = []) => {
  const sanitizedRedirect = sanitizeRedirect(redirect);

  if (sanitizedRedirect && !redirectWhitelistCheck(sanitizedRedirect, whitelist)) return undefined;

  return sanitizedRedirect;
};

const normaliseUsername = (username: string): string => {
  if (EMAIL_PATTERN.test(username)) return username;
  if (isValidPhoneNumber(username, 'GB'))
    return formatNumber(username, 'GB', 'INTERNATIONAL', { humanReadable: false }).replace(/ /g, '');
  throw new Error('Invalid username');
};

const isEmailDomainAllowed = (email: string, allowedDomains: string[]): boolean => {
  if (allowedDomains.length === 0) return true;

  const domain = email.slice(email.lastIndexOf('@') + 1).toLowerCase();

  return allowedDomains.some((allowedDomain) => {
    const normalisedAllowedDomain = allowedDomain.toLowerCase().replace(/^\./, '');
    return domain === normalisedAllowedDomain || domain.endsWith(`.${normalisedAllowedDomain}`);
  });
};

export {
  decodeState,
  EMAIL_OR_PHONE_NUMBER_PATTERN,
  EMAIL_PATTERN,
  encodeState,
  getClientId,
  isEmailDomainAllowed,
  normaliseUsername,
  PHONE_NUMBER_PATTERN,
  validateRedirect,
};
